import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  CanvasPopover,
  CanvasPopoverContent,
  CanvasPopoverTrigger,
} from '@/components/ui/canvas-popover';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Plus, TextCursorInput, ScanEye } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import {
  NodeStruct,
  CustomNode,
  NodeInputPort,
  NodeOutputPort,
  NodeParam,
  NodeFile,
  NodeCategory,
} from '@/types/node-types';
import { useReactFlow } from '@xyflow/react';
import {
  getCategoryConfig,
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { ConnectionType, CustomConnection } from '@/types/edge-types';
import { NodeSelector } from '@/pages/workflow/component/controls/node-selector';
import { ConnectedNodeStatus } from './connected-node-status';
import { NodeHandle } from './node-handle';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { truncateFilename } from '@/utils/text-utils';
import { FileIconTile } from '@/pages/workflow/component/file-icon-tile';
import { ARTIFACT_ELIGIBLE_CATEGORIES } from '@/pages/workflow/helper/artifact-node-manager';

type PortType = NodeInputPort | NodeOutputPort;

interface SlotCardProps {
  nodeId: string;
  port: PortType;
  portDirection: 'input' | 'output';
  edgeType: ConnectionType;
  title?: string;
  paramField?: NodeParam; // Optional: the param field if this is a bindable param input port
  onOpenFilesPreview?: () => void; // Optional: handler to open files preview modal
  isLocked?: boolean; // Optional: whether the node is locked (passed from parent)
}

export const SlotCard: React.FC<SlotCardProps> = ({
  nodeId,
  port,
  portDirection,
  edgeType,
  title,
  paramField,
  onOpenFilesPreview,
  isLocked: nodeIsLocked = false,
}) => {
  const isOutput = portDirection === 'output';
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const {
    edges,
    nodes,
    setNodes: setStoreNodes,
    onConnect,
    addNode,
    getCompatibleNodes,
    getCompatibleSourceNodes,
    selectNode: storeSelectNode,
  } = useNodeStore();

  // Subscribe to isLoadingNodeList to trigger recomputation when nodes finish loading
  const isLoadingNodeList = useNodeStore((state) => state.isLoadingNodeList);

  const { getNode } = useReactFlow();
  const { openWorkflowPanel, setHighlightedField, setActiveTab } =
    useWorkflowPanelStore();
  const { fieldErrors, clearFieldError } = useWorkflowValidationStore();

  // Get the current node to access result data
  const currentNode = getNode(nodeId) as CustomNode;

  // Get files from result for output ports
  const resultFiles = useMemo(() => {
    if (!isOutput || !currentNode?.data?.result) return [];
    return (currentNode.data.result as { files?: NodeFile[] })?.files || [];
  }, [isOutput, currentNode?.data?.result]);

  const hasFiles = resultFiles.length > 0;
  const fileCount = resultFiles.length;

  const hasArtifactCards = useMemo(() => {
    if (!hasFiles) return false;
    const nodeType = currentNode?.data?.struct?.node_type;
    if (!nodeType) return false;
    return ARTIFACT_ELIGIBLE_CATEGORIES.has(nodeType);
  }, [hasFiles, currentNode?.data?.struct?.node_type]);

  // Track previous file count to detect new files
  const prevFileCountRef = useRef<number>(0);
  const [shouldBlink, setShouldBlink] = useState(false);

  // When we add a node with stacking, we place it with a fallback height first; once React Flow
  // reports its measured height we adjust Y so it doesn't overlap (Task = above, others = below).
  const pendingStackAdjustRef = useRef<{
    nodeId: string;
    desiredX: number;
    isTaskSlot: boolean;
    topY: number;
    bottomY: number;
    GAP: number;
  } | null>(null);

  // Apply vertical position adjustment once the newly stacked node has measured height.
  // Reads fresh nodes from the store to avoid overwriting nodes added by concurrent
  // multi-add iterations (forEach loop in handleAddAllSelected).
  useEffect(() => {
    const pending = pendingStackAdjustRef.current;
    if (!pending) return;
    const node = nodes.find((n) => n.id === pending.nodeId);
    const height = node?.measured?.height;
    if (!node || height == null) return;
    const newY = pending.isTaskSlot
      ? pending.topY - height - pending.GAP
      : pending.bottomY + pending.GAP;
    const freshNodes = useNodeStore.getState().nodes;
    setStoreNodes(
      freshNodes.map((n) =>
        n.id === pending.nodeId
          ? { ...n, position: { ...n.position, x: pending.desiredX, y: newY } }
          : n,
      ),
    );
    pendingStackAdjustRef.current = null;
  }, [nodes, setStoreNodes]);

  // Detect when files are newly added and stop blinking after 10 seconds
  useEffect(() => {
    if (isOutput && hasFiles) {
      const currentFileCount = resultFiles.length;
      const prevFileCount = prevFileCountRef.current;

      // If files were just added (went from 0 to >0, or count increased)
      if (prevFileCount === 0 && currentFileCount > 0) {
        setShouldBlink(true);
        // Stop blinking after 10 seconds, but keep badge visible
        const timeout = setTimeout(() => {
          setShouldBlink(false);
        }, 10000);

        // Update ref before returning to ensure it's always updated
        prevFileCountRef.current = currentFileCount;

        return () => clearTimeout(timeout);
      }

      // Update ref for next comparison (when file count changes but wasn't 0->N)
      prevFileCountRef.current = currentFileCount;
    } else {
      // Reset when no files or not output port
      prevFileCountRef.current = 0;
      setShouldBlink(false);
    }
  }, [isOutput, hasFiles, resultFiles.length]);

  // Button should not be locked if it's an output port with files (view-only operation)
  const isLocked = nodeIsLocked && !(isOutput && hasFiles);

  const { id: portId, display_name: displayName } = port;

  // Check if this field has a validation error
  // Validation errors use display_name || field_id as fieldName, so we need to check both
  const hasValidationError = fieldErrors.some(
    (error) =>
      error.nodeId === nodeId &&
      (error.fieldName === (paramField?.display_name || paramField?.field_id) ||
        error.fieldId === (paramField?.field_id || portId) ||
        error.fieldName === displayName),
  );
  // Input ports have category, output ports category is not correct so hardcode it for now
  const category = isOutput
    ? 'output'
    : 'category' in port
      ? port.category
      : '';

  // Get nodes for the selector
  // isLoadingNodeList triggers recomputation when nodes finish loading
  const selectorNodes = useMemo(() => {
    if (!currentNode?.data?.struct) return [];

    if (isLoadingNodeList) return [];

    // For output ports, get compatible nodes that can receive this output
    return isOutput
      ? getCompatibleNodes(currentNode.data.struct)
      : getCompatibleSourceNodes(currentNode.data.struct, portId);
  }, [
    isOutput,
    currentNode,
    portId,
    getCompatibleNodes,
    getCompatibleSourceNodes,
    isLoadingNodeList,
  ]);

  // Get category-specific styling and configuration
  const config = getCategoryConfig(
    category,
    portId,
    title || displayName,
    port.port_type.accepted_types || [],
  );

  const connectedNodes = useMemo(() => {
    if (isOutput) {
      // For output ports, find nodes connected from this source
      return edges
        .filter(
          (edge) => edge.source === nodeId && edge.sourceHandle === portId,
        )
        .map((edge) => {
          const node = nodes.find((n) => n.id === edge.target);
          return node?.data?.struct ?? null;
        })
        .filter((n): n is NodeStruct => n !== null);
    }
    // For input ports, find nodes connected to this target
    return edges
      .filter((edge) => edge.target === nodeId && edge.targetHandle === portId)
      .map((edge) => {
        const node = nodes.find((n) => n.id === edge.source);
        return node?.data?.struct ?? null;
      })
      .filter((n): n is NodeStruct => n !== null);
  }, [edges, nodeId, portId, nodes, isOutput]);

  // Check if we should show the "Write to" option
  // This is shown when:
  // 1. This is a param's bindable input port (paramField is provided)
  // 2. The field.show is true (meaning the input is visible in the configure panel)
  const showWriteToOption = paramField && paramField.show;

  // // Handler for "Write to" option click
  const handleWriteToClick = () => {
    if (!paramField) return;

    // Use unified selection action (updates nodes[].selected and opens console panel)
    storeSelectNode(nodeId);

    // Open the configure tab with node data
    openWorkflowPanel('configure', { nodeId });
    setHighlightedField(nodeId, paramField.field_id);

    // Close the popover
    setIsOpen(false);
  };

  // Function to validate if a node can be connected.
  // Reads fresh edges from the store so multi-add (forEach in handleAddAllSelected)
  // sees edges created by prior iterations for accurate cardinality checks.
  const validateNodeConnection = (selectedNode: NodeStruct): string | null => {
    if (!currentNode) return 'Current node not found';

    const freshEdges = useNodeStore.getState().edges;

    if (isOutput) {
      // For output ports: validate connection to selected node's input
      const sourcePort = currentNode.data.struct.output_ports.find(
        (p) => p.id === portId,
      );
      if (!sourcePort) return 'Source port not found';

      // Find a compatible input port on the selected node
      const targetPort =
        selectedNode.input_ports.find((inputPort) => {
          const sourceTypes = sourcePort.port_type.accepted_types;
          const targetTypes = inputPort.port_type.accepted_types;
          return sourceTypes.some((sourceType) =>
            targetTypes.includes(sourceType),
          );
        }) ||
        selectedNode.params.find((param) => {
          if (!param.bindable_config?.input_port) return false;
          const sourceTypes = sourcePort.port_type.accepted_types;
          const targetTypes =
            param.bindable_config.input_port.port_type.accepted_types;
          return sourceTypes.some((sourceType) =>
            targetTypes.includes(sourceType),
          );
        })?.bindable_config?.input_port;

      if (!targetPort) return 'No compatible input port found';

      // Check cardinality
      if (targetPort.cardinality === 'one') {
        const existingConnections = freshEdges.filter(
          (edge) => edge.targetHandle === targetPort.id,
        );
        if (existingConnections.length > 0) {
          return `Target port only accepts one connection but already has ${existingConnections.length} connection(s)`;
        }
      }
    } else {
      // For input ports: validate connection from selected node's output
      const sourcePort = selectedNode.output_ports[0];
      if (!sourcePort) return 'Selected node has no output ports';

      const sourceTypes = sourcePort.port_type.accepted_types;
      const targetTypes = port.port_type.accepted_types;

      const hasTypeMatch = sourceTypes.some((sourceType) =>
        targetTypes.includes(sourceType),
      );

      if (!hasTypeMatch) {
        return `Type mismatch: ${selectedNode.display_name} outputs ${sourceTypes.join(', ')} but this port expects ${targetTypes.join(', ')}`;
      }

      // Check cardinality
      if (port.cardinality === 'one') {
        const existingConnections = freshEdges.filter(
          (edge) => edge.target === nodeId && edge.targetHandle === portId,
        );
        if (existingConnections.length > 0) {
          return `This port only accepts one connection but already has ${existingConnections.length} connection(s)`;
        }
      }
    }

    return null; // No validation errors
  };

  const handleNodeSelect = (selectedNode: NodeStruct) => {
    try {
      // Validate the connection first
      const error = validateNodeConnection(selectedNode);
      if (error) {
        setValidationError(error);
        return;
      }

      // Clear any previous validation errors
      setValidationError(null);

      if (!currentNode) {
        console.error('Current node not found for ID:', nodeId);
        return;
      }

      // Read fresh state so multi-add (forEach) sees nodes added by prior iterations
      const { nodes: freshNodes, edges: freshEdges } = useNodeStore.getState();

      let newNodeId: string;

      if (isOutput) {
        // For output ports (not Tool nodes): create node to the right and connect to it's param input port
        const newNodePosition = {
          x: currentNode.position.x + 350, // 350px to the right
          y: currentNode.position.y + 300, // 300px down
        };

        newNodeId = addNode(selectedNode, newNodePosition);

        // Find the compatible target param input port
        // Note: target will always be a param's input_port (not a regular tool input_port)
        const sourcePort = currentNode.data.struct.output_ports.find(
          (p) => p.id === portId,
        );
        const targetParam = selectedNode.params.find((param) => {
          if (!param.bindable_config?.input_port) return false;
          const sourceTypes = sourcePort?.port_type.accepted_types || [];
          const targetTypes =
            param.bindable_config.input_port.port_type.accepted_types;
          return sourceTypes.some((sourceType) =>
            targetTypes.includes(sourceType),
          );
        });

        const targetHandleId =
          targetParam?.bindable_config?.input_port?.id || 'input';

        const connection: CustomConnection = {
          source: nodeId,
          target: newNodeId,
          sourceHandle: portId,
          targetHandle: targetHandleId,
          edge_type: ConnectionType.PORT_TO_PARAM,
          target_param_id: targetParam?.field_id,
        };

        onConnect(connection);

        // Clear validation error for this field after successful connection
        if (targetParam) {
          const fieldId = targetParam.field_id;
          clearFieldError(nodeId, fieldId);
        }
      } else {
        // For input ports: place to the left, same height; stack below any nodes already connected to this agent
        const desiredX = currentNode.position.x - 350;
        const defaultY = currentNode.position.y;
        const NODE_WIDTH = 300; // Match .function-node width in generic-node/style.css
        const NODE_HEIGHT = 200; // Fallback for new node height when stacking (no fixed height in CSS)
        const GAP = 12;

        const connectedToThisAgent = new Set(
          freshEdges.filter((e) => e.target === nodeId).map((e) => e.source),
        );
        const existingConnected = freshNodes.filter((n) =>
          connectedToThisAgent.has(n.id),
        );
        const columnRight = desiredX + NODE_WIDTH;
        const existingInColumn = existingConnected.filter((n) => {
          const w = n.measured?.width ?? NODE_WIDTH;
          const nLeft = n.position.x;
          const nRight = n.position.x + w;
          return nRight > desiredX && nLeft < columnRight;
        });

        const isTaskSlot = config.title === 'Task';
        const topY =
          existingInColumn.length > 0
            ? Math.min(...existingInColumn.map((n) => n.position.y))
            : defaultY;
        const bottomY =
          existingInColumn.length > 0
            ? Math.max(
                ...existingInColumn.map(
                  (n) => n.position.y + (n.measured?.height ?? NODE_HEIGHT),
                ),
              )
            : defaultY;
        const initialY =
          existingInColumn.length > 0
            ? isTaskSlot
              ? topY - NODE_HEIGHT - GAP
              : bottomY + GAP
            : defaultY;

        const newNodePosition = { x: desiredX, y: initialY };

        newNodeId = addNode(selectedNode, newNodePosition);

        if (existingInColumn.length > 0) {
          pendingStackAdjustRef.current = {
            nodeId: newNodeId,
            desiredX,
            isTaskSlot,
            topY,
            bottomY,
            GAP,
          };
        }

        const sourceHandleId = selectedNode.output_ports[0]?.id || 'output';
        const connection: CustomConnection = {
          source: newNodeId,
          target: nodeId,
          sourceHandle: sourceHandleId,
          targetHandle: config.handleId,
          target_param_id: paramField?.field_id || null,
          edge_type: edgeType,
        };

        onConnect(connection);

        // Clear validation error for this field after successful connection
        if (paramField) {
          const fieldId = paramField.field_id;
          clearFieldError(nodeId, fieldId);
        }
      }

      // Select the new node and open configure tab
      storeSelectNode(newNodeId);
      setActiveTab('configure');

      // Keep popover open until user blurs for Tool slot only; close on select for others
      if (category !== NodeCategory.TOOL) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error(`Error creating node:`, error);
    }
  };

  // Clear validation errors and reset background when popover closes
  const handlePopoverChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setValidationError(null);
    }
  };
  // Handle open files preview if files exist, otherwise open popover
  const handleOpenFilesPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOutput && hasFiles && onOpenFilesPreview) {
      onOpenFilesPreview();
    }
  };

  // Render file preview for output ports with files
  const renderFilePreview = () => {
    if (!isOutput || !hasFiles) return null;

    if (fileCount === 1) {
      // Single file - show file icon, filename, and file size (like preview-slot-card.tsx)
      const file = resultFiles[0];
      const FileIcon = getFileIcon(file.name);
      const backgroundColor = getFileIconBackgroundColor(file.name);

      return (
        <>
          <FileIconTile
            icon={FileIcon}
            backgroundColor={backgroundColor}
            size='small'
            iconColor='#ffffff'
          />
          <div className='flex-1 min-w-0 flex flex-col items-start pl-1'>
            <span className='text-sm font-medium max-w-full text-black dark:text-white! truncate'>
              {truncateFilename(file.name, 22)}
            </span>
          </div>
        </>
      );
    } else {
      // Multiple files - show first 3 file icon tiles, plus a "more files" indicator if > 3
      const maxDisplayIcons = 3;
      const displayFiles = resultFiles.slice(0, maxDisplayIcons);
      const overlapOffset = 20;
      const hasMoreFiles = fileCount > maxDisplayIcons;
      const remainingCount = fileCount - maxDisplayIcons;
      const totalTiles = hasMoreFiles
        ? displayFiles.length + 1
        : displayFiles.length;

      return (
        <>
          <div
            className='flex items-center shrink-0 relative'
            style={{
              width: `${32 + (totalTiles - 1) * overlapOffset}px`,
              height: '32px',
            }}
          >
            {displayFiles.map((file, index) => {
              const FileIcon = getFileIcon(file.name);
              const backgroundColor = getFileIconBackgroundColor(file.name);
              return (
                <FileIconTile
                  key={`${file.name}-${index}`}
                  icon={FileIcon}
                  backgroundColor={backgroundColor}
                  size='small'
                  iconColor='#ffffff'
                  className={`absolute ${
                    index > 0
                      ? 'shadow-[-4px_0px_10px_-4px_rgba(0,0,0,0.8)]'
                      : ''
                  }`}
                  style={{
                    left: `${index * overlapOffset}px`,
                    zIndex: index,
                  }}
                />
              );
            })}
            {hasMoreFiles && (
              <div
                className='absolute flex items-center justify-center rounded-lg border border-gray-400 bg-white shrink-0 w-8 h-8'
                style={{
                  left: `${displayFiles.length * overlapOffset}px`,
                  zIndex: displayFiles.length,
                  boxShadow:
                    displayFiles.length > 0
                      ? '-4px 0px 10px -4px rgba(0,0,0,0.8)'
                      : undefined,
                }}
              >
                <div className='flex items-center text-black justify-center'>
                  <Plus className='w-3! h-3! font-bold' />
                  <span className='text-sm font-semibold text-black leading-none'>
                    {remainingCount}
                  </span>
                </div>
              </div>
            )}
          </div>
          <TruncateTextWithTooltip
            text={`${fileCount} Files`}
            className='text-sm font-medium pl-1 min-w-0 dark:text-white!'
            tooltipProps={{
              side: 'left',
              align: 'start',
              sideOffset: 50,
              className: 'max-w-xs',
            }}
          />
        </>
      );
    }
  };

  // Get tooltip text for disabled state
  const getDisabledTooltip = () => {
    if (isLocked) return 'Workflow is locked';
    return '';
  };

  const slotContent = (
    <>
      {isOutput && hasFiles && !hasArtifactCards && (
        <span
          className={`absolute top-0 right-3 w-3 h-3 bg-red-500 rounded-full z-10 ${
            shouldBlink ? 'animate-pulse' : ''
          }`}
        ></span>
      )}
      <CanvasPopover
        open={isOpen && !(isOutput && hasFiles)}
        onOpenChange={handlePopoverChange}
      >
        <CanvasPopoverTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            disabled={isLocked}
            className={`nodrag group flex items-center w-full h-full gap-1 pl-0.5 py-1 mb-[6px] rounded-xl relative transition-colors duration-200 bg-muted-background hover:bg-neutral-200 dark:hover:bg-neutral-600 border-2 ${isLocked ? 'cursor-not-allowed opacity-60' : ''} ${hasValidationError ? 'border-red-500' : 'border-transparent'}`}
            onClick={handleOpenFilesPreview}
          >
            <NodeHandle
              port={{
                id: config.handleId,
                display_name: displayName,
                info: 'info' in port ? port.info : '',
                port_type: port.port_type,
              }}
              nodeId={nodeId}
              direction={portDirection}
              config={config}
              edgeType={edgeType}
              isLocked={isLocked}
              hasValidationError={hasValidationError}
            />

            {/* File preview for output ports with files, otherwise show connected node status */}
            {isOutput && hasFiles ? (
              renderFilePreview()
            ) : (
              <ConnectedNodeStatus
                connectedNodes={connectedNodes}
                config={config}
                category={category}
                portDirection={portDirection}
              />
            )}

            {/* View Files / Add Text and Plus icon on hover / Required when validation error */}
            <div className='ml-auto flex items-center gap-2 shrink-0 max-w-[100px]'>
              {isOutput && hasFiles && !hasArtifactCards ? (
                <>
                  <span
                    className='text-xs font-normal text-right truncate text-muted-foreground'
                    title='View Files'
                  >
                    View Files
                  </span>
                  <ScanEye className='w-4 h-4 shrink-0 text-muted-foreground' />
                </>
              ) : (
                <div
                  className='flex items-center gap-2 min-w-0 w-full'
                  onClick={
                    showWriteToOption
                      ? (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleWriteToClick();
                        }
                      : undefined
                  }
                >
                  <span
                    className={`text-xs font-normal text-right truncate min-w-0 ${
                      hasValidationError
                        ? 'text-red-500'
                        : 'hidden group-hover:block! text-muted-foreground'
                    }`}
                    style={{
                      display: isOpen || hasValidationError ? 'block' : 'none',
                    }}
                    title={hasValidationError ? 'Required' : config.addText}
                  >
                    {hasValidationError ? 'Required' : config.addText}
                  </span>
                  <Plus
                    className={`w-4 h-4 shrink-0 ${
                      hasValidationError
                        ? 'text-red-500'
                        : 'text-muted-foreground hidden group-hover:block!'
                    }`}
                    style={{
                      display: isOpen || hasValidationError ? 'block' : 'none',
                    }}
                  />
                </div>
              )}
            </div>
          </Button>
        </CanvasPopoverTrigger>
        <CanvasPopoverContent
          className='min-w-[320px] w-full rounded-xl p-0 max-w-[380px]'
          align='center'
          side={isOutput ? 'bottom' : 'top'}
          sideOffset={10}
        >
          <>
            {validationError && (
              <div className='mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700'>
                {validationError}
              </div>
            )}

            <NodeSelector
              nodes={selectorNodes}
              primaryColor={config.styling.primaryColor}
              onNodeClick={handleNodeSelect}
              placeholder={
                isOutput
                  ? 'Search compatible nodes...'
                  : `Search ${config.title.toLowerCase()}...`
              }
              listTitle={'Compatible nodes'}
              isLoading={isLoadingNodeList}
              customAction={
                showWriteToOption
                  ? {
                      icon: TextCursorInput,
                      label: `Write a ${paramField?.display_name}`,
                      onClick: handleWriteToClick,
                      primaryColor: config.styling.primaryColor,
                    }
                  : undefined
              }
            />
          </>
        </CanvasPopoverContent>
      </CanvasPopover>
    </>
  );

  return (
    <div key={`${nodeId}-${portId}`} className='px-4 relative'>
      {isLocked ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{slotContent}</div>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{getDisabledTooltip()}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        slotContent
      )}
    </div>
  );
};
